import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { collection, query, getDocs, orderBy, updateDoc, doc, Timestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { Button } from '../ui/button';
import {
  ChevronUp,
  Calendar,
  User,
  Filter,
  Search,
  Loader2,
  Trash2
} from 'lucide-react';

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  voteCount: number;
  createdBy: string;
  createdByEmail: string;
  createdByNickname?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

interface RequestsManagementProps {
  customerId: string;
  brandColor: string;
  onViewRequest: (request: FeatureRequest) => void;
}

export default function RequestsManagement({ customerId, brandColor, onViewRequest }: RequestsManagementProps) {
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [deletingRequest, setDeletingRequest] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
  }, [customerId]);

  useEffect(() => {
    // Apply filters
    let filtered = [...requests];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        r => r.title.toLowerCase().includes(query) ||
             r.description.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(r => r.category === categoryFilter);
    }

    setFilteredRequests(filtered);
  }, [requests, searchQuery, statusFilter, categoryFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const requestsRef = collection(db, 'customers', customerId, 'requests');
      const q = query(requestsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const requestsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as FeatureRequest[];

      setRequests(requestsList);
      setFilteredRequests(requestsList);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId: string, newStatus: string) => {
    setUpdatingStatus(requestId);
    try {
      await updateDoc(doc(db, 'customers', customerId, 'requests', requestId), {
        status: newStatus,
        updatedAt: Timestamp.now(),
      });

      // Update local state
      setRequests(prev =>
        prev.map(r => r.id === requestId ? { ...r, status: newStatus, updatedAt: Timestamp.now() } : r)
      );
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (requestId: string, requestTitle: string) => {
    if (!confirm(`Are you sure you want to delete "${requestTitle}"? This action cannot be undone and will also delete all votes, comments, and internal notes associated with this request.`)) {
      return;
    }

    setDeletingRequest(requestId);
    try {
      // Delete the request and all its subcollections
      // Note: Firestore security rules will handle cascade deletion
      // or we need to delete subcollections manually

      // Delete votes subcollection
      const votesRef = collection(db, 'customers', customerId, 'requests', requestId, 'votes');
      const votesSnapshot = await getDocs(votesRef);
      const voteDeletePromises = votesSnapshot.docs.map(voteDoc => deleteDoc(voteDoc.ref));
      await Promise.all(voteDeletePromises);

      // Delete comments subcollection
      const commentsRef = collection(db, 'customers', customerId, 'requests', requestId, 'comments');
      const commentsSnapshot = await getDocs(commentsRef);
      const commentDeletePromises = commentsSnapshot.docs.map(commentDoc => deleteDoc(commentDoc.ref));
      await Promise.all(commentDeletePromises);

      // Delete internal notes subcollection
      const notesRef = collection(db, 'customers', customerId, 'requests', requestId, 'internalNotes');
      const notesSnapshot = await getDocs(notesRef);
      const noteDeletePromises = notesSnapshot.docs.map(noteDoc => deleteDoc(noteDoc.ref));
      await Promise.all(noteDeletePromises);

      // Finally delete the request itself
      await deleteDoc(doc(db, 'customers', customerId, 'requests', requestId));

      // Update local state
      setRequests(prev => prev.filter(r => r.id !== requestId));

      toast.success('Request deleted successfully');
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request. Please try again.');
    } finally {
      setDeletingRequest(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-12">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: brandColor }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
            >
              <option value="all">All Categories</option>
              <option value="feature">Feature</option>
              <option value="improvement">Improvement</option>
              <option value="bug_fix">Bug Fix</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Active Filters Info */}
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <span>Showing {filteredRequests.length} of {requests.length} requests</span>
          {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setCategoryFilter('all');
              }}
              className="text-primary hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {filteredRequests.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            {requests.length === 0 ? (
              <>
                <p className="text-lg mb-2">No feature requests yet</p>
                <p className="text-sm">
                  Requests will appear here once users start submitting them from your voting portal
                </p>
              </>
            ) : (
              <>
                <p className="text-lg mb-2">No requests match your filters</p>
                <p className="text-sm">Try adjusting your search or filter criteria</p>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Request
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Votes
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => onViewRequest(request)}
                  >
                    <td className="px-4 py-4">
                      <div className="max-w-md">
                        <p className="font-medium text-foreground truncate">{request.title}</p>
                        <p className="text-sm text-muted-foreground truncate mt-1">
                          {request.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {request.createdByNickname || request.createdByEmail}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground capitalize">
                        {request.category.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={request.status}
                        onChange={(e) => handleStatusChange(request.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={updatingStatus === request.id}
                        className={`text-xs font-medium px-2.5 py-1.5 rounded-full capitalize cursor-pointer border-0 ${getStatusColor(request.status)}`}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <ChevronUp className="h-4 w-4" style={{ color: brandColor }} />
                        <span className="font-semibold text-foreground">{request.voteCount}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(request.createdAt.toDate()).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(request.id, request.title);
                          }}
                          disabled={deletingRequest === request.id}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          {deletingRequest === request.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
